// Booking funnel widget — visualises the drop-off from rooms grid → complete
// booking, so the owner can spot where visitors bail. Data comes from
// `GET /api/admin/analytics/funnel`.
import React, { useCallback, useEffect, useState } from 'react';
import { Filter, Loader2, RefreshCw } from 'lucide-react';
import { apiCall } from './_shared';

const RANGES = [7, 30, 90];

const stepColors = [
  'bg-slate-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-emerald-700',
];

const BookingFunnelWidget = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const j = await apiCall(`/api/admin/analytics/funnel?days=${days}`);
      setData(j);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.steps || []).map((s, i) => (
    <div key={s.event} className="mb-3" data-testid={`funnel-step-${s.event}`}>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="font-medium text-gray-900">
          <span className="inline-block w-6 text-gray-400 font-normal">{i + 1}.</span>
          {s.label}
        </span>
        <span className="text-gray-500">
          <span className="font-semibold text-gray-900">{s.count.toLocaleString()}</span>
          {' · '}
          {s.pct_of_top}% of top
          {s.drop_off_pct_from_prev !== null && s.drop_off_pct_from_prev > 0 && (
            <span className="ml-2 text-red-600 font-semibold">
              ▼ {s.drop_off_pct_from_prev}% drop
            </span>
          )}
        </span>
      </div>
      <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`${stepColors[i] || 'bg-emerald-500'} h-full transition-all duration-500`}
          style={{ width: `${s.pct_of_top}%` }}
        />
      </div>
    </div>
  ));

  return (
    <div className="bg-white rounded-xl border p-5 shadow-sm" data-testid="booking-funnel-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-gray-900">Booking funnel</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg" data-testid="funnel-range-tabs">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setDays(r)}
                className={`px-2.5 py-1 text-xs font-semibold rounded transition-all ${
                  days === r ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
                data-testid={`funnel-range-${r}`}
              >
                {r}d
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50"
            data-testid="funnel-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-gray-500" />}
          </button>
        </div>
      </div>

      {err && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-3">{err}</div>
      )}

      {!data ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : rows}

      {data && (
        <div className="border-t mt-3 pt-3 flex items-center justify-between">
          <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Overall conversion</span>
          <span className="text-lg font-bold text-emerald-600" data-testid="funnel-overall-conversion">
            {data.overall_conversion_pct}%
          </span>
        </div>
      )}
    </div>
  );
};

export default BookingFunnelWidget;
